import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import countries from "i18n-iso-countries";
import * as R from "ramda";
import { addServiceData, updateServiceData, deleteServiceData } from "../../../../../actions/services-api";
import { generateLettersForUserIdAndUserServiceId } from "../../../../../actions/events-api";

import ServiceData from "../../../../../constants/models/serviceData";
import { ConnectionTypes, convertConnectionTypesToServiceType } from "../../../../../constants/utils/connectionTypes";
import { PostalCityInput } from "../../../../../_common/postalCityInput";

export const EducationInstitutionDetails = ({ index, educationInstitution, user, removeEducationInstitution, generateRelocationLetters }) => {
	countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));
	const dispatch = useDispatch();

	const formRef = useRef(null);

	let initCollapsedState = true;
	let initState = {
		type: "Etudiant",
		name: "",
		address: {
			streetAddress: "",
			streetAddress2: "",
			postalCode: "",
			addressLocality: "",
			addressCountry: "FR",
		},
		level: "",
		label: "",
	};

	if (!R.isNil(educationInstitution.id)) {
		initState = {
			...educationInstitution.payload,
			address: {
				streetAddress: educationInstitution.payload?.address?.streetAddress || "",
				streetAddress2: educationInstitution.payload?.address?.streetAddress2 || "",
				postalCode: educationInstitution.payload?.address?.postalCode || "",
				addressLocality: educationInstitution.payload?.address?.addressLocality || "",
				addressCountry: educationInstitution.payload?.address?.addressCountry || "FR",
			},
		};
		initCollapsedState = false;
	}

	const [payload, setPayload] = useState(initState);
	const [collapsed, setCollapsed] = useState(initCollapsedState);

	const handleSave = (e) => {
		e.preventDefault();
		if (formRef.current.checkValidity()) {
			let serviceData = new ServiceData({
				userId: user.user.id,
				serviceType: convertConnectionTypesToServiceType(ConnectionTypes.etudiant),
				serviceId: "etudiant" + index,
				payload: payload,
				lifeEvent: payload.lifeEvent || 1,
			});

			if (R.isNil(educationInstitution.id)) {
				dispatch(addServiceData(serviceData)).then((s) => {
					if (generateRelocationLetters) {
						dispatch(generateLettersForUserIdAndUserServiceId(user.user.id, s.id, "relocation"));
					}
				});
			} else {
				serviceData.id = educationInstitution.id;
				dispatch(updateServiceData(serviceData)).then((s) => {
					if (generateRelocationLetters) {
						dispatch(generateLettersForUserIdAndUserServiceId(user.user.id, s.id, "relocation"));
					}
				});
			}

			setCollapsed(!collapsed);
		}
		formRef.current.classList.add("was-validated");
	};

	const handleDelete = (e) => {
		e.preventDefault();
		if (R.isNil(educationInstitution.id)) {
			removeEducationInstitution(educationInstitution);
		} else {
			dispatch(deleteServiceData(educationInstitution.id, ConnectionTypes.etudiant));
		}
	};

	return (
		<section className={"card card-body" + (index === 0 ? "" : " mt-4")} id={"educationInstitution" + index}>
			<div className="card-header p-0" id="headingUserDetail" role="button" onClick={() => setCollapsed(!collapsed)}>
				<div className="flex-row align-items-center justify-content-lg-between">
					<h4 className="h3 font-weight-black container-tight my-auto">{index === 0 ? "Votre établissement d'enseignement principal" : "Autre établissement"}</h4>
					{!R.isNil(payload.name) && (
						<span className="mr-auto align-middle">
							<i className="eva eva-arrow-right mx-2 align-middle o-30"></i>
							<span className="o-60 align-middle">{payload.name}</span>
						</span>
					)}
					<button type="button" className="btn btn-icon p-0 text-uppercase font-weight-demi fs-4 my-auto ml-auto m-lg-1 o-40" name="refPrincipal">
						<i className={"eva " + (!collapsed ? "eva-arrow-ios-upward-outline" : "eva-arrow-ios-downward-outline")}></i>
					</button>
				</div>
			</div>

			<div className={"collapse" + (collapsed ? " show" : "")}>
				<form className="block needs-validation" noValidate ref={formRef}>
					{/* Nom de l'établissement */}
					<h5 className="h4 text-uppercase text-secondary font-weight-black mb-sm mt-4">Nom de l'établissement</h5>
					<div className="row gx-2 mt-n2">
						<div className="col-12 col-md-5">
							<div className="form-group">
								<label className="h6 font-weight-bolder my-auto" htmlFor={"name" + index}>
									Nom de l'établissement
								</label>
								<input required type="text" className="form-control" id={"name" + index} placeholder="Établissement" value={payload.name} onChange={(e) => setPayload({ ...payload, name: e.target.value })} />
								<div className="invalid-feedback">Ce champ est requis</div>
							</div>
						</div>

						<div className="col-12 col-md-7">
							<div className="form-group">
								<label className="h6 font-weight-bolder my-auto" htmlFor={"Adresseentreprise" + index}>
									Adresse ligne 1
								</label>
								<input required type="text" className="form-control" id={"Adresseentreprise" + index} placeholder="Adresse de l'établissement" value={payload.address.streetAddress} onChange={(e) => setPayload({ ...payload, address: { ...payload.address, streetAddress: e.target.value } })} />
								<div className="invalid-feedback">Ce champ est requis</div>
							</div>

							<div className="form-group">
								<label className="label h6 font-weight-bold my-auto" htmlFor={"Adresseligne2" + index}>
									Adresse ligne 2
								</label>
								<input type="text" className="form-control rounded" id={"Adresseligne2" + index} placeholder="(optionnel)" value={payload.address.streetAddress2} onChange={(e) => setPayload({ ...payload, address: { ...payload.address, streetAddress2: e.target.value } })} />
							</div>

							<div className="form-row">
								<PostalCityInput
									postalCode={payload.address.postalCode}
									city={payload.address.addressLocality}
									onChange={(updatedAddress) => setPayload({ ...payload, address: { ...payload.address, postalCode: updatedAddress.postalCode, addressLocality: updatedAddress.city } })}
									hasWrapper={false}
									country={payload.address.addressCountry}
									customClasses={{ city: "form-group col-12 col-sm-6 col-lg", postalCode: "form-group col-12 col-sm-6 col-lg" }}
								/>

								{/* <div className="form-group col-12 col-sm-6 col-lg">
									<label className="h6 font-weight-bolder my-auto" htmlFor={"Postalentreprise" + index} required>
										Code Postal
									</label>
									<input required type="text" className="form-control" id={"Postalentreprise" + index} placeholder="ex: 75001" value={payload.address.postalCode} onChange={(e) => setPayload({ ...payload, address: { ...payload.address, postalCode: e.target.value } })} />
									<div className="invalid-feedback">Ce champ est requis</div>
								</div>

								<div className="form-group col-12 col-sm-6 col-lg">
									<label className="h6 font-weight-bolder my-auto" htmlFor={"Villeentreprise" + index} required>
										Ville
									</label>
									<input required type="text" className="form-control" id={"Villeentreprise" + index} placeholder="ex: Paris" value={payload.address.addressLocality} onChange={(e) => setPayload({ ...payload, address: { ...payload.address, addressLocality: e.target.value } })} />
									<div className="invalid-feedback">Ce champ est requis</div>
								</div> */}

								<div className="form-group col-12 col-sm-6 col-lg">
									<label className="h6 font-weight-bolder my-auto" htmlFor={"Paysentreprise" + index}>
										Pays
									</label>
									<select className="custom-select" id={"Paysentreprise" + index} value={payload.address.addressCountry} onChange={(e) => setPayload({ ...payload, address: { ...payload.address, addressCountry: e.target.value } })}>
										{Object.keys(countries.getAlpha2Codes()).map((countryCode, idx) => (
											<option key={idx} value={countryCode}>
												{countries.getName(countryCode, "fr", { select: "official" })}
											</option>
										))}
									</select>
								</div>
							</div>
						</div>
					</div>

					<hr className="mb-gutter" />
					<h5 className="h4 text-uppercase text-secondary font-weight-black mb-sm">Cursus</h5>
					<div className="row gx-2 mt-n2">
						<div className="col-12 col-md">
							<div className="form-group">
								<label className="h6 font-weight-bolder my-auto" htmlFor={"niveau" + index}>
									Niveau d'étude
								</label>
								<input required type="text" className="form-control" id={"niveau" + index} placeholder="ex: L1" value={payload.level} onChange={(e) => setPayload({ ...payload, level: e.target.value })} />
								<div className="invalid-feedback">Ce champ est requis</div>
							</div>
						</div>
						<div className="col-12 col-md">
							<div className="form-group">
								<label className="h6 font-weight-bolder my-auto" htmlFor={"intitule"}>
									Intitulé de la formation
								</label>
								<input required type="text" className="form-control" id={"intitule"} placeholder="Intitulé" value={payload.label} onChange={(e) => setPayload({ ...payload, label: e.target.value })} />
							</div>
						</div>
					</div>

					<div className="card-footer text-right px-0">
						<button className="btn btn-sm btn-alert-info border-none loading-btn" onClick={handleSave}>
							Enregistrer
							<div className="loading loading-spinner spinner-border spinner-border-sm ml-2 mr-n1"></div>
						</button>
						{index !== 0 && (
							<span className="btn btn-sm btn-icon px-xs btn-alert-danger ml-1 border-none" onClick={handleDelete}>
								<i className="eva eva-trash-outline"></i>
							</span>
						)}
					</div>
				</form>
			</div>
		</section>
	);
};
